/** Pane sizes, remembered per browser.
 *
 * Clamped on read as well as on write: a stored value from a larger window would
 * otherwise leave a pane wider than the screen with no way to drag it back.
 */

export interface Layout {
  sidebar: number;
  assist: number;
  terminal: number;
}

export const DEFAULT_LAYOUT: Layout = { sidebar: 240, assist: 320, terminal: 220 };

const LIMITS: Record<keyof Layout, [number, number]> = {
  sidebar: [160, 560],
  assist: [220, 680],
  terminal: [80, 900],
};

const KEY = "fss.layout.v1";

/** The editor is the point of the screen; no side pane may squeeze it out. */
export const MIN_EDITOR_WIDTH = 320;
const MIN_EDITOR_HEIGHT = 160;
const SPLITTERS = 10; // the two 5px drag strips between the columns
const STATUS_BAR = 22;

/** Viewport, injected so the maths is testable without a browser. */
export interface Viewport {
  width: number;
  height: number;
}

function viewport(): Viewport {
  return { width: window.innerWidth, height: window.innerHeight };
}

export function clamp(
  pane: keyof Layout,
  value: number,
  layout: Layout = DEFAULT_LAYOUT,
  view: Viewport = viewport(),
): number {
  const [min, max] = LIMITS[pane];
  let ceiling: number;
  if (pane === "terminal") {
    ceiling = view.height - STATUS_BAR - MIN_EDITOR_HEIGHT - 5;
  } else {
    const other = pane === "sidebar" ? layout.assist : layout.sidebar;
    ceiling = view.width - other - SPLITTERS - MIN_EDITOR_WIDTH;
  }
  // On a very small window the ceiling can fall below the minimum. The minimum
  // wins: a pane clamped to something negative would vanish with no way back.
  ceiling = Math.min(max, Math.max(min, ceiling));
  return Math.round(Math.min(ceiling, Math.max(min, value)));
}

export function loadLayout(): Layout {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_LAYOUT };
    const parsed = JSON.parse(raw) as Partial<Layout>;
    const merged: Layout = {
      sidebar: parsed.sidebar ?? DEFAULT_LAYOUT.sidebar,
      assist: parsed.assist ?? DEFAULT_LAYOUT.assist,
      terminal: parsed.terminal ?? DEFAULT_LAYOUT.terminal,
    };
    return {
      sidebar: clamp("sidebar", merged.sidebar, merged),
      assist: clamp("assist", merged.assist, merged),
      terminal: clamp("terminal", merged.terminal, merged),
    };
  } catch {
    // Private windows and blocked site data both throw here; a default layout is
    // a fine outcome, a blank screen is not.
    return { ...DEFAULT_LAYOUT };
  }
}

export function saveLayout(layout: Layout): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(layout));
  } catch {
    /* not worth surfacing: the layout simply will not persist */
  }
}
